import { clientInstance } from '../../../../services/graphql.service';
import { i18n } from '../../../../services/translation.service';

import GenericStore from '../../../../models/store/generic-store.model';
import {
  addressSuggestion,
  dealers,
  specificModel,
  testDriveRequestsCalendar,
  testDriveBookingVehicleSearchWithinRadius,
  dealerSearch,
  users,
  usersEmail,
  testDriveBookingVehicleSearch,
} from '../../../../graphql/queries';

import { useStore } from '../../../../composables/store.composable';

import {
  CONTEXT,
  SEARCH_PROFILE,
  MONTHS_TO_CALL,
  MODEL_TYPE_URL_SEPARATOR,
} from '../../../../models/constants';

import {
  createTestDriveBooking,
  createRequest,
} from '../../../../graphql/mutations';
import {
  MutationCreateRequestArgs,
  MutationCreateTestDriveBookingArgs,
  QueryUsersArgs,
  QueryUsersEmailArgs,
  VehicleType,
} from '../../../../graphql/schema';
import { TestDriveMode } from '../../../../models/utilities/testdrive-mode.enum';
import ConfigurationState from '../../../../models/store/modules/configuration-state.model';

import DateService from '../../../../services/date.service';
import CalendarService from '../../../../services/calendar.service';
import { PrepareSelectionDataService } from '../../../../services/prepare-selection-data.service';
import UtilsService from '../../../../services/utils.service';

const { client } = clientInstance();

const getAddressSuggestion = async (
  { commit }: GenericStore,
  { address }: any
): Promise<void> => {
  const { state } = useStore();
  const variables: any = {
    countryCode: state.configuration.locale.countryCode,
    market: state.configuration.market,
    address,
  };

  client
    .query(addressSuggestion, variables)
    .toPromise()
    .then((value) => {
      if (value.data?.suggestions) {
        const { suggestions } = UtilsService.cleanTypenames(value.data);
        commit('addressSuggestion', suggestions);
      }
    });
};

const getTestDriveFromHomeWhitelistedDealers = async ({
  commit,
}: GenericStore): Promise<void> => {
  const { state } = useStore();

  // Determine locationId and locationQuery values based on priority logic
  const locationId = state.otbcFlow.initialLocation?.locationId || null;
  let locationQuery =
    state.otbcFlow.locationQueryFreeText ||
    state.otbcFlow.requestLocationQueryFreeText;

  // If locationId is present, then nullify locationQuery
  if (locationId) {
    locationQuery = null;
  }

  // If locationId is not present, then consider locationQuery
  if (locationId && locationQuery) {
    locationQuery = null;
  }

  const variables: any = {
    countryCode: state.configuration.locale.countryCode,
    language: state.configuration.locale.language,
    locationId,
    // radius: state.otbcFlow.locationRadius,
    radius: '1000km', // workaround for now while we don't have a postal code mandatory in the flow
    searchProfileName: state.configuration.searchProfileName,
    pickMeUp: true,
  };

  const value = await client.query(dealerSearch, variables).toPromise();

  if (value.error) return;

  const whitelistedDealers = value.data?.dealerSearch?.dealers;
  if (whitelistedDealers) {
    let whitelistedDealersIDs = whitelistedDealers.reduce(
      (result: string[], dealer: any) => [...result, dealer.id],
      []
    );

    // in case it is multiple fixed dealers we filter the results
    if (
      state.otbcFlow.dealers &&
      state.otbcFlow.dealers.length > 0 &&
      state.otbcFlow.dealers[0] !== ''
    )
      whitelistedDealersIDs = whitelistedDealersIDs.filter((tdfhDealers) =>
        state.otbcFlow.dealers.includes(tdfhDealers)
      );

    commit('testDriveFromHomeWhitelistedDealers', whitelistedDealersIDs);
  }
};

const getTestDriveBookingVehicleSearchWithinRadius = async ({
  commit,
  dispatch,
}: GenericStore): Promise<void> => {
  const { state, getter } = useStore();

  if (getter?.isTestDriveFromHomeEnabled)
    dispatch('getTestDriveFromHomeWhitelistedDealers');

  const endDate = DateService.formatDateForVBS(
    DateService.addMonths(new Date(), MONTHS_TO_CALL)
  );
  if (!state.configuration.bookingActive) {
    commit('bookingSearchFinished', true);
    commit('didBookingSearch', true);
    return;
  }

  // Determine locationId and locationQuery values based on priority logic
  const locationId = state.otbcFlow.initialLocation?.locationId || null;
  let locationQuery =
    state.otbcFlow.locationQueryFreeText ||
    state.otbcFlow.requestLocationQueryFreeText;

  // If locationId is present, then nullify locationQuery
  if (locationId) {
    locationQuery = null;
  }

  // If locationId is not present, then consider locationQuery
  if (locationId && locationQuery) {
    locationQuery = null;
  }

  const variables: any = {
    countryCode: state.configuration.locale.countryCode,
    language: state.configuration.locale.language,
    market: state.configuration.market,
    context: state.configuration.context,
    modelSeriesId: state.otbcFlow.model.id,
    endDate: endDate,
    vehicleTypes: state.otbcFlow.model.modelType
      ?.split(MODEL_TYPE_URL_SEPARATOR)
      .filter((vehicle) => vehicle in VehicleType),
    startDate: DateService.formatDateForVBS(
      state.otbcFlow.selectedDay ?? new Date()
    ),
    locationId: locationId,
    locationQuery: locationQuery,
    radius: state.otbcFlow.locationRadius,
    searchProfileName: state.configuration.searchProfileName,
    pickMeUp: getter?.isTestDriveFromHomeEnabled,
  };

  const value = await client
    .query(testDriveBookingVehicleSearchWithinRadius, variables)
    .toPromise();

  if (value.error) {
    commit('errorMessage', value.error.toString());
    commit('didBookingSearch', true);
    return;
  }

  if (value.data?.testDriveBookingVehicleSearchWithinRadius) {
    let { bookings, dealers } = UtilsService.cleanTypenames(
      value.data.testDriveBookingVehicleSearchWithinRadius
    );

    // in case it is multiple fixed dealers we filter the results
    if (
      state.otbcFlow.dealers &&
      state.otbcFlow.dealers.length > 0 &&
      state.otbcFlow.dealers[0] !== ''
    ) {
      bookings = bookings.filter((booking) =>
        state.otbcFlow.dealers.includes(booking.dealerId)
      );
      dealers = dealers.filter((dealer) =>
        state.otbcFlow.dealers.includes(dealer.id)
      );
    }

    const matchedData = bookings
      .map((booking) => {
        const dealer = dealers.find((d) => d.id === booking.dealerId);
        return { ...booking, dealer };
      })
      // Sorts available Bookings by Dealer Distance to Location
      .sort(
        (bookingA, bookingB) =>
          bookingA.dealer.address.distance - bookingB.dealer.address.distance
      );

    commit('bookingsSearchResults', matchedData);
    commit('dealersSearchResults', dealers);
    commit('requestDealersSearchResults', dealers);
    commit('filteredBookingsSearchResults', matchedData);
    commit('didBookingSearch', true);
  }
  commit('bookingSearchFinished', true);
};

const getRequestTestDriveDealersWithinRadius = async ({
  commit,
}: GenericStore): Promise<void> => {
  const { state } = useStore();

  const endDate = DateService.formatDateForVBS(
    DateService.addMonths(new Date(), MONTHS_TO_CALL)
  );
  // Determine locationId and locationQuery values based on priority logic
  const locationId = state.otbcFlow.initialLocation?.locationId || null;
  let locationQuery =
    state.otbcFlow.requestLocationQueryFreeText ||
    state.otbcFlow.locationQueryFreeText ||
    null;
  // Priority to locationId, if present
  if (locationId) locationQuery = null;

  let isRadiusMandatory = true;
  if (state.otbcFlow.dealers && state.otbcFlow.dealers.length > 1)
    isRadiusMandatory = false;

  const variables = {
    countryCode: state.configuration.locale.countryCode,
    language: state.configuration.locale.language,
    context: state.configuration.context,
    market: state.configuration.market,
    modelSeriesId: state.otbcFlow.model.id,
    endDate: endDate,
    vehicleTypes: state.otbcFlow.model.modelType
      ?.split(MODEL_TYPE_URL_SEPARATOR)
      .filter((vehicle) => vehicle in VehicleType),
    startDate: DateService.formatDateForVBS(
      state.otbcFlow.selectedDay ?? new Date()
    ),
    locationId,
    locationQuery,
    radius: isRadiusMandatory ? state.otbcFlow.locationRadius : '1000km', // effectively ignoring the radius on test drive from home and multi-dealer scenarios
    searchProfileName: state.configuration.searchProfileName,
    pickMeUp: false,
  };
  const value = await client
    .query(testDriveBookingVehicleSearchWithinRadius, variables)
    .toPromise();

  if (value.error) {
    commit('errorMessage', value.error.toString());
    return;
  }
  if (value.data?.testDriveBookingVehicleSearchWithinRadius) {
    let { dealers } = UtilsService.cleanTypenames(
      value.data.testDriveBookingVehicleSearchWithinRadius
    );

    // in case it is multiple fixed dealers we filter the results
    if (
      state.otbcFlow.dealers &&
      state.otbcFlow.dealers.length > 0 &&
      state.otbcFlow.dealers[0] !== ''
    ) {
      dealers = dealers.filter((dealer) =>
        state.otbcFlow.dealers.includes(dealer.id)
      );
    }

    commit('requestDealersSearchResults', dealers);
  }
  commit('locationSelected', true);
};

const getTestDriveBookingVehicleSearchFixedDealer = async ({
  state: {
    model: { modelSeries },
  },
  commit,
  dispatch,
}: GenericStore): Promise<void> => {
  const { state, getter } = useStore();

  if (getter?.isTestDriveFromHomeEnabled)
    dispatch('getTestDriveFromHomeWhitelistedDealers');

  const endDate = DateService.formatDateForVBS(
    DateService.addMonths(new Date(), MONTHS_TO_CALL)
  );
  const startDate = DateService.formatDateForVBS(
    state.otbcFlow.selectedDay ?? new Date()
  );

  const modelSeriesId = modelSeries || state.otbcFlow.model.id;

  const variables: any = {
    countryCode: state.configuration.locale.countryCode,
    language: state.configuration.locale.language,
    context: state.configuration.context,
    modelSeriesId,
    startDate,
    endDate,
    vehicleTypes: state.otbcFlow.model.modelType
      ?.split(MODEL_TYPE_URL_SEPARATOR)
      .filter((vehicle) => vehicle in VehicleType),
    dealerId: state.otbcFlow.dealer,
    pickMeUp: getter?.isTestDriveFromHomeEnabled,
    searchProfileName: SEARCH_PROFILE.PRIVATE,
  };

  const value = await client
    .query(testDriveBookingVehicleSearch, variables)
    .toPromise();

  if (value.error) {
    commit('errorMessage', value.error.toString());
    commit('didBookingSearch', true);
    commit('fixedDealer', '');
    return;
  }

  if (value.data?.testDriveBookingVehicleSearchFixedDealer) {
    const { bookings } = UtilsService.cleanTypenames(
      value.data.testDriveBookingVehicleSearchFixedDealer
    );
    const dealer = state.otbcFlow.selectedDealer;
    const bookingsWithDealer = bookings.map((booking) => {
      return { ...booking, dealer };
    });
    commit('bookingsSearchResults', bookingsWithDealer);
    commit('filteredBookingsSearchResults', bookingsWithDealer);
    commit('dealersSearchResults', [dealer]);
    commit('selectedDealer', dealer);
    commit('selectedModelMotorisations', state.otbcFlow.model.motorisations);
    commit('bookingSearchFinished', true);
    commit('didBookingSearch', true);
  } else {
    commit('fixedDealer', '');
  }
};

const getSpecificModel = async ({
  state: {
    model: { modelSeries },
  },
  commit,
}: GenericStore): Promise<any> => {
  const { state } = useStore();
  const variables: any = {
    countryCode: state.configuration.locale.countryCode,
    language: state.configuration.locale.language,
    context: state.configuration.context,
    vehicleTypes: state.otbcFlow.model.modelType
      ?.split(MODEL_TYPE_URL_SEPARATOR)
      .filter((vehicle) => vehicle in VehicleType),
    modelSeriesId: modelSeries,
    archivedMotorisations: true,
  };

  client
    .query(specificModel, variables)
    .toPromise()
    .then((value) => {
      if (value.data?.specificModel) {
        const { specificModel } = UtilsService.cleanTypenames(value.data);
        commit('model', { ...state.otbcFlow.model, ...specificModel });
      }
    });
};

const getDealerData = async ({
  state: { dealer },
  commit,
}: GenericStore): Promise<any> => {
  const { state } = useStore();
  const dealerId = dealer.baseInfo?.outletId || dealer;
  const variables: any = {
    countryCode: state.configuration.locale.countryCode,
    language: state.configuration.locale.language,
    context: state.configuration.context,
    id: dealerId,
  };

  client
    .query(dealers, variables)
    .toPromise()
    .then((value) => {
      if (value.data?.dealers) {
        const { dealers } = UtilsService.cleanTypenames(value.data);
        commit('selectedDealer', dealers);
      }
    });
};

const selectInitialLocationFreeText = async (
  { commit }: GenericStore,
  { locationQuery }: any
): Promise<void> => {
  commit('initialLocation', {
    label: locationQuery,
    locationId: '',
    postalCode: '',
  });
  // We need to call this to get the dealers within the radius so that we can determine
  // when the label needs to come up as empty in the request screen in case of no dealers are found here

  commit('locationSelected', true);
  commit('addressSuggestion', []);
};

const selectInitialLocation = async (
  { commit }: GenericStore,
  { suggestion }: any
): Promise<void> => {
  if (suggestion) {
    commit('initialLocation', suggestion);
  }
  // We need to call this to get the dealers within the radius so that we can determine
  // when the label needs to come up as empty in the request screen in case of no dealers are found here

  commit('locationSelected', true);
  commit('addressSuggestion', []);
  commit('requestLocation', suggestion);
};

const selectRequestLocation = async (
  { commit }: GenericStore,
  { suggestion }: any
): Promise<void> => {
  const { dispatch } = useStore();
  commit('locationSelected', false);
  commit('requestLocation', suggestion);
  commit('addressSuggestion', []);
  dispatch('getRequestTestDriveDealersWithinRadius');
};

const selectLocationRadius = async (
  { commit }: GenericStore,
  { locationRadius }: any
): Promise<void> => {
  commit('locationRadius', locationRadius);
};

const getTestDriveRequestsCalendar = async (): Promise<void> => {
  const { state, commit } = useStore();
  const {
    configuration: { calendarStartDate, calendarWeekDayStart },
  } = state;
  const startDate = DateService.formatDateForVBS(
    CalendarService.firstDayOfWeek(calendarStartDate, calendarWeekDayStart)
  );

  const endDate = DateService.formatDateForVBS(
    DateService.addMonths(calendarStartDate, MONTHS_TO_CALL)
  );

  const variables: any = {
    endDate,
    dealerId:
      state.otbcFlow.dealer?.baseInfo?.outletId || state.otbcFlow.dealer,
    countryCode: state.configuration.locale.countryCode,
    context: state.configuration.context,
    language: state.configuration.locale.language,
    startDate,
  };

  client
    .query(testDriveRequestsCalendar, variables)
    .toPromise()
    .then((value) => {
      if (value.data?.testDriveRequestsCalendar) {
        commit(
          'dealerCalendar',
          UtilsService.cleanTypenames(value.data.testDriveRequestsCalendar)
        );
      }
    });
};

const getCiamId = async (
  { state: _ }: GenericStore,
  payload: QueryUsersArgs
): Promise<any> => {
  const variables: QueryUsersArgs = { ...payload };

  try {
    const { data } = await client?.query(users, variables).toPromise();
    return data?.users?.ciamUid || '';
  } catch (e) {
    console.error('Invalid User (getCiamId)', e);
    return '';
  }
};

const getCiamIdByEmailAndBuildProspect = async (
  { state }: GenericStore,
  payload: string
): Promise<any> => {
  const variables: QueryUsersEmailArgs = { email: payload };
  const prospect = state?.otbcFlow?.prospect || { ciamId: null };

  try {
    const { data } = await client?.query(usersEmail, variables).toPromise();
    if (data?.usersEmail?.userId) {
      prospect.ciamId = data?.usersEmail?.userId;
    }
    return prospect;
  } catch (e) {
    console.error('getCiamIdByEmail error', e);
    return prospect;
  }
};

const selectDealer = async (
  { commit, dispatch }: GenericStore,
  { dealer }: any
): Promise<void> => {
  await commit('dealer', dealer);
  dispatch('getDealerData');
  getTestDriveRequestsCalendar();
};

const sendAemForm = async (
  { dispatch, commit }: GenericStore,
  configuration: ConfigurationState
): Promise<void> => {
  let mutationResult;
  const { state } = useStore();
  if (state.otbcFlow.testDriveMode == TestDriveMode.BOOKING) {
    mutationResult = await dispatch('sendBooking', configuration);
  } else {
    mutationResult = await dispatch('sendRequest', configuration);
  }

  commit('appointment', mutationResult);
  //trackFormSubmissionEvent(rootState, rootGetters.isVANSContext); // TODO: [Tracking]
};

const sendRequest = async (
  { state, commit }: GenericStore,
  configuration: ConfigurationState
): Promise<any> => {
  const variables: MutationCreateRequestArgs = {
    testDriveRequestAttempt: PrepareSelectionDataService.buildRequest(
      state,
      configuration
    ),
  };

  commit('informationSent', variables.testDriveRequestAttempt);

  const {
    data: { createRequest: response },
  } = await client.mutation(createRequest, variables).toPromise();

  if (!response) {
    commit('errorMessage', i18n.t('error.client.BAD_REQUEST'));
  }

  return UtilsService.cleanTypenames(response);
};

const sendBooking = async (
  { state, commit }: GenericStore,
  configuration: ConfigurationState
): Promise<any> => {
  const variables: MutationCreateTestDriveBookingArgs = {
    testDriveBookingAppointmentAttempt:
      PrepareSelectionDataService.buildBooking(state, configuration),
  };

  commit('informationSent', variables.testDriveBookingAppointmentAttempt);

  const {
    data: { createTestDriveBooking: response },
  } = await client.mutation(createTestDriveBooking, variables).toPromise();

  if (!response) {
    commit('errorMessage', i18n.t('error.client.BAD_REQUEST'));
  }

  return UtilsService.cleanTypenames(response);
};

export const actions = {
  getAddressSuggestion,
  getDealerData,
  getSpecificModel,
  selectInitialLocation,
  selectInitialLocationFreeText,
  selectRequestLocation,
  selectLocationRadius,
  selectDealer,
  getTestDriveRequestsCalendar,
  sendAemForm,
  sendRequest,
  sendBooking,
  getCiamId,
  getCiamIdByEmailAndBuildProspect,
  getTestDriveBookingVehicleSearchWithinRadius,
  getTestDriveBookingVehicleSearchFixedDealer,
  getRequestTestDriveDealersWithinRadius,
  getTestDriveFromHomeWhitelistedDealers,
};
